import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import {
    IGetListAchievementsParams,
    IAchievement,
    IResponseAchievements,
} from '@/features/student/StudentAchievements/interface';
import { DEFAULT_FIRST_PAGE } from '@/common/constants';
import { IPagination } from '@/common/interface';
import { LIMIT_FOR_PAGINATION } from '@/features/student/StudentAchievements/constants';

// Saga Actions
export const fetchAchievementsRequest = createAction<IGetListAchievementsParams>(
    'achievements/fetchAchievementsRequest',
);
export const fetchAchievementsSuccess = createAction<IResponseAchievements>('achievements/fetchAchievementsSuccess');
export const fetchAchievementsFailure = createAction<string>('achievements/fetchAchievementsFailure');
export const fetchLastAchievementRequest = createAction('achievements/fetchLastAchievementRequest');
export const fetchLastAchievementSuccess = createAction<IAchievement>('achievements/fetchLastAchievementSuccess');
export const fetchLastAchievementFailure = createAction<string>('achievements/fetchLastAchievementFailure');

const DEFAULT_ACHIEVEMENT_LIST_QUERY: IGetListAchievementsParams = {
    page: DEFAULT_FIRST_PAGE,
    page_size: LIMIT_FOR_PAGINATION,
};
const DEFAULT_ACHIEVEMENT_PAGINATION: IPagination = {
    count: 0,
    page: DEFAULT_FIRST_PAGE,
    page_size: LIMIT_FOR_PAGINATION,
    total_pages: 0,
};
interface AchievementsState {
    query: IGetListAchievementsParams;
    pagination: IPagination;
    achievements: IAchievement[];
    lastAchievement: IAchievement | null;
    loading: {
        isLoadingGetList: boolean;
        isLoadingGetLastAchievement: boolean;
    };
    error: {
        errorGetList?: string;
        errorGetLastAchievement?: string;
    };
}

const initialState: AchievementsState = {
    query: DEFAULT_ACHIEVEMENT_LIST_QUERY,
    pagination: DEFAULT_ACHIEVEMENT_PAGINATION,
    achievements: [],
    lastAchievement: null,
    loading: {
        isLoadingGetList: false,
        isLoadingGetLastAchievement: false,
    },
    error: {
        errorGetList: undefined,
        errorGetLastAchievement: undefined,
    },
};

const achievementsSlice = createSlice({
    name: 'achievements',
    initialState,
    reducers: {
        setQuery: (state, action: PayloadAction<IGetListAchievementsParams>) => {
            state.query = action.payload;
        },
        setPagination: (state, action: PayloadAction<IPagination>) => {
            state.pagination = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Fetch achievements
        builder
            .addCase(fetchAchievementsRequest, (state) => {
                state.loading.isLoadingGetList = true;
                state.error.errorGetList = undefined;
            })
            .addCase(fetchAchievementsSuccess, (state, action) => {
                state.loading.isLoadingGetList = false;
                state.error.errorGetList = undefined;

                state.achievements = action.payload.results || [];
                state.pagination = {
                    page: action.payload.page,
                    page_size: action.payload.page_size,
                    count: action.payload.count,
                    total_pages: Math.ceil(action.payload.count / action.payload.page_size),
                };
            })
            .addCase(fetchAchievementsFailure, (state, action) => {
                state.loading.isLoadingGetList = false;
                state.error.errorGetList = action.payload;
            })
            .addCase(fetchLastAchievementRequest, (state) => {
                state.loading.isLoadingGetLastAchievement = true;
                state.error.errorGetLastAchievement = undefined;
            })
            .addCase(fetchLastAchievementSuccess, (state, action) => {
                state.loading.isLoadingGetLastAchievement = false;
                state.error.errorGetLastAchievement = undefined;
                state.lastAchievement = action.payload;
            })
            .addCase(fetchLastAchievementFailure, (state, action) => {
                state.loading.isLoadingGetLastAchievement = false;
                state.error.errorGetLastAchievement = action.payload;
            });
    },
});

export const { setQuery, setPagination } = achievementsSlice.actions;

export default achievementsSlice.reducer;
